/**
 * Sentence case for table column/row headers (matches Word export).
 *
 * Only rewrites text that is fully shouting-case (no lowercase letters and no
 * digits) — genuine ALL CAPS headers like "STUDY PROCEDURES AND VISITS".
 * Anything that already has a lowercase letter (e.g. "ECHO or MUGA",
 * "PM060184 Dose", "Absolute Neutrophil Count (ANC)") or contains a digit
 * (drug codes, doses) is returned unchanged, since lowering the whole string
 * would otherwise destroy embedded acronyms and codes.
 */
export function toSentenceCaseTable(s) {
  const t = String(s ?? '').trim();
  if (!t) return '';
  if (/[a-z]/.test(t) || /\d/.test(t)) return t;
  const lower = t.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

const TITLE_CASE_SMALL_WORDS = new Set([
  'a', 'an', 'the', 'and', 'or', 'but', 'of', 'in', 'on', 'for', 'to', 'with', 'at', 'by',
]);

/**
 * Title case for table/figure captions (matches Word export): first/last word always
 * capitalized, short connecting words lowercased elsewhere.
 */
export function toTitleCaseCaption(s) {
  const words = String(s ?? '').trim().split(/\s+/).filter(Boolean);
  return words
    .map((w, i) => {
      const lower = w.toLowerCase();
      if (i > 0 && i < words.length - 1 && TITLE_CASE_SMALL_WORDS.has(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
}

/**
 * Detects an already-authored table caption line (e.g. "Table 3-1: Summary of...") so its
 * label/title can be restyled and title-cased without inventing numbering that isn't in the
 * source text.
 */
const TABLE_CAPTION_LINE_RE = /^(Table\s+[\w.-]+)[:.]?\s+(.+)$/i;

export function parseTableCaptionLine(text) {
  const raw = String(text ?? '').trim();
  // "Table 3-1 summarizes the estimand..." also matches the caption shape, but it is an
  // in-text cross-reference sentence, not a caption: treating it as one would title-case
  // it and render it bold. Captions put ':' straight after the label; cross-references
  // continue into a verb. See isCrossReferenceSentence below.
  if (isCrossReferenceSentence(raw)) return null;
  const match = TABLE_CAPTION_LINE_RE.exec(raw);
  if (!match) return null;
  return { label: `${match[1]}:`, title: toTitleCaseCaption(match[2]) };
}

/**
 * A table/figure in-text cross-reference sentence — "Table 3-1 summarizes the estimand
 * for the primary endpoint." — is body prose, not a caption, so it must render at normal
 * weight. Generation models routinely emit it wrapped in <strong>/<b> (or markdown '**')
 * anyway, which bakes the bold into stored section HTML.
 *
 * This strips the emphasis from those sentences only. It deliberately does NOT touch:
 *  - real caption lines ("Table 3-1: Estimand for the Primary Objective"), which stay bold
 *    — they're distinguished by the ':' immediately after the label;
 *  - bold anywhere else in the content (headings, table header cells, inline bold terms).
 */

// "Table 3-1 summarizes ...", "Figure 1-1 shows ..." — label, then a space and a verb,
// i.e. a sentence. A caption instead has ':'/'.' right after the label, so requiring
// whitespace before the verb is what separates the two cases.
const CROSS_REF_SENTENCE_RE =
  /^(?:Table|Figure)\s+[\w.-]+\s+(?:summarizes|summarises|presents|lists|shows|describes|provides|details|outlines|displays|reports)\b/i;

const stripTags = (html) => String(html ?? '').replace(/<[^>]*>/g, '');

const decodeBasicEntities = (s) =>
  String(s ?? '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");

/** True when a block's text reads as a table/figure cross-reference sentence. */
export function isCrossReferenceSentence(text) {
  return CROSS_REF_SENTENCE_RE.test(decodeBasicEntities(stripTags(text)).trim());
}

/**
 * Removes <strong>/<b> wrappers and markdown '**' emphasis from table/figure
 * cross-reference sentences in a section's HTML, leaving all other bold intact.
 */
export function unboldCrossReferenceSentences(html) {
  let out = String(html ?? '');
  if (!out) return out;

  // Case 1: the emphasis is inside a block element — <p><strong>Table 3-1 summarizes…</strong></p>.
  // Unwrap the emphasis only when the block's whole text is the cross-reference sentence.
  out = out.replace(
    /<(p|div|li|h[1-6])\b([^>]*)>([\s\S]*?)<\/\1>/gi,
    (match, tag, attrs, inner) => {
      if (!isCrossReferenceSentence(inner)) return match;
      const unwrapped = inner
        .replace(/<\/?(?:strong|b)\b[^>]*>/gi, '')
        .replace(/\*\*/g, '');
      return `<${tag}${attrs}>${unwrapped}</${tag}>`;
    }
  );

  // Case 2: the emphasis wraps the block — <strong><p>Table 3-1 summarizes…</p></strong>
  // (and the bare, unwrapped <strong>Table 3-1 summarizes…</strong> form).
  out = out.replace(
    /<(strong|b)\b[^>]*>([\s\S]*?)<\/\1>/gi,
    (match, _tag, inner) => (isCrossReferenceSentence(inner) ? inner : match)
  );

  // Case 3: markdown emphasis on a line of its own — **Table 3-1 summarizes…**
  out = out.replace(/\*\*([^*\n]+)\*\*/g, (match, inner) =>
    isCrossReferenceSentence(inner) ? inner : match
  );

  return out;
}
